import { Button, Dialog, Typography } from "@mui/material"
import CloseButton from "common/CloseButton"
import { selectedAccessoriesSelector } from "components/shopFeatures/accessories/accessoriesSelectors"
import { selectedColorSelector } from "components/shopFeatures/color/colorSelectors"
import { selectedDetailingSelector } from "components/shopFeatures/detailing/detailingSelectors"
import { selectedUpholsterySelector } from "components/shopFeatures/upholstery/upholsterySelectors"
import { Font } from "components/shopFeatures/wizard/constants"
import { setIsDialogOpen } from "components/shopFeatures/wizard/wizardSlice"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import { setClientLoggedIn } from "../authSlice"
import OnlinePrices from "./OnlinePrices"

const StyledContainer = styled.div`
  background-color: ${(p) => p.theme.colors.white};
  position: relative;
  padding: 20px;
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 20px 50px;
  }
`
const StyledTitle = styled(Typography).attrs({ variant: "h2" })`
  font-family: ${Font.OENaPro};
  font-size: 24px;
  font-weight: 400;
  line-height: 34px;
  letter-spacing: 0.05em;
  text-align: center;
`
const StyledButton = styled(Button)`
  width: 100%;
  background: black;

  :hover,
  :active {
    background: black;
  }

  :focus {
    background: ${(p) => p?.theme?.colors?.disable};
    color: white;
    border: none;
    outline: none;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 10px;
    margin-bottom: 20px;
  }
`
// const StyledHeading = styled(Typography).attrs({
//   variant: "h2",
//   component: "h3",
// })`
//   font-family: ${Font.OENaPro};
//   font-size: 20px;
//   font-weight: 300;
//   line-height: 32px;
//   letter-spacing: 0.03em;
// `

const StyledDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(p) => p.theme.colors.lightGray};
  margin: ${(p) => p.theme.spacing(2, 0)};
  color: black;
  margin: 15px 0;
`

const StyledSubText = styled.p`
  font-family: ${Font.OENaPro};
  font-size: 18px;
  font-weight: 500;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  line-height: 1;
`
const StyledPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledInfoSubText = styled(StyledSubText)`
  margin: 30px 0 0 0;
`

const OnlineBenefits = () => {
  const { model } = useTypedSelector((state) => state.wizard)
  const isPreSale = model?.typeId === 3
  const detailing = useTypedSelector(selectedDetailingSelector)
  const color = useTypedSelector(selectedColorSelector)
  const upholstery = useTypedSelector(selectedUpholsterySelector)
  const [openDialog, setOpenDialog] = useState(false)
  const accessories = useTypedSelector(selectedAccessoriesSelector)
  const { licensingFee, price } = useTypedSelector((state) => state.wizard)
  const {
    auth: { clientLoggedIn, authenticated, ...auth },
    wizard: { isDialogOpen },
  } = useTypedSelector((state) => state)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!clientLoggedIn || (price && price.discount <= 0)) return

    setOpenDialog(true)

    dispatch(setClientLoggedIn(false))
  }, [clientLoggedIn, dispatch, price])

  const handleOnClose = () => {
    setOpenDialog(false)
    dispatch(setIsDialogOpen(false))
  }

  if (!detailing || !licensingFee) return null

  return (
    <Dialog
      open={openDialog || (authenticated && isDialogOpen)}
      onClose={handleOnClose}
      aria-labelledby="dialog-title"
      maxWidth="md"
    >
      <StyledContainer>
        <CloseButton onClick={handleOnClose} />

        <StyledTitle id="dialog-title">
          היי {auth?.userInfo?.firstName},
        </StyledTitle>

        <StyledSubText>
          <Image
            src="/icons/Discount.svg"
            alt="Discount"
            width={17}
            height={17}
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
          />
          קיבלת הטבות רכישה אונליין
        </StyledSubText>

        {/* {price && (
          <>
            <OnlinePrices
              label={"מחיר מחירון כולל אגרת רישוי"}
              price={detailing.price.originalPrice + licensingFee.totalPrice}
            />
          </>
        )} */}
        <StyledPriceContainer>
          {" "}
          {/* Create an array of items to display with numbers */}
          {(() => {
            // Build an array of benefit items to display
            const benefitItems: React.ReactNode[] = []
            let itemCounter = 0

            // Add detailing item if it has a discount
            if (detailing && detailing.price.discount > 0) {
              benefitItems.push(
                <OnlinePrices
                  key="detailing"
                  itemNumber={++itemCounter}
                  label={`הנחה מיוחדת על סך ${detailing.price.discount} ₪`}
                  price={-detailing.price.discount}
                />
              )
            }

            // Add color item if applicable
            if (
              color &&
              color.price.originalPrice > 0 &&
              color.price.totalPrice != color.price.originalPrice
            ) {
              benefitItems.push(
                <OnlinePrices
                  key="color"
                  itemNumber={++itemCounter}
                  label={color.details.name}
                  price={color.price}
                />
              )
            }

            // Add upholstery item if applicable
            if (
              upholstery &&
              upholstery.price.originalPrice > 0 &&
              upholstery.price.totalPrice != upholstery.price.originalPrice
            ) {
              benefitItems.push(
                <OnlinePrices
                  key="upholstery"
                  itemNumber={++itemCounter}
                  label={upholstery.details.name}
                  price={upholstery.price}
                />
              )
            }

            // Add accessory items
            const accessoryItems = accessories
              .filter((accessory) => accessory.price.discount > 0)
              .map((accessory) => (
                <OnlinePrices
                  key={accessory.details.code}
                  itemNumber={++itemCounter}
                  label={accessory.details.name}
                  price={accessory.price}
                />
              ))

            benefitItems.push(...accessoryItems)

            // Return all the items
            return benefitItems
          })()}
        </StyledPriceContainer>

        <StyledDivider />

        {price && (
          <>
            {/* <OnlinePrices
              label={'סה"כ לתשלום לאחר הטבות'}
              price={price.totalPrice}
              final
            /> */}

            {price?.discount > 0 && (
              <OnlinePrices
                label={
                  isPreSale ? `סה"כ הטבת ההשקה` : "סה״כ חיסכון ברכישה אונליין"
                }
                price={price?.discount}
                finalDiscount
              />
            )}
          </>
        )}
        <StyledDivider />

        <StyledInfoSubText>
          ההטבה תישמר לך מרגע תשלום המקדמה באתר
        </StyledInfoSubText>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleOnClose}
        >
          מעולה, תודה
        </StyledButton>
      </StyledContainer>
    </Dialog>
  )
}

export default OnlineBenefits
